<template>
  <div class="entity-search-wrap">
    <TextInput
      v-model="searchQuery"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
    />
    <div v-if="showDropdown" class="entity-dropdown">
      <div v-if="isSearching" class="entity-dropdown-item muted">Searching...</div>
      <div v-else-if="results.length === 0" class="entity-dropdown-item muted">No results found.</div>
      <button
        v-for="option in results"
        type="button"
        class="entity-dropdown-item"
        @mousedown.prevent="selectResult(option)"
      >
        <span class="entity-name">{{ option.name || option.external_id }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { TextInput } from '@stakeui';

export default {
  name: 'EntitySearchDropdown',
  components: {
    TextInput,
  },
  props: {
    current: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Search by name or email',
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    debounceMs: {
      type: Number,
      default: 250,
    },
  },
  data() {
    return {
      searchQuery: '',
      results: [],
      isSearching: false,
      showDropdown: false,
      searchDebounceTimer: null,
    };
  },
  watch: {
    current: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.searchQuery = '';
          return;
        }
        this.searchQuery = newValue.name || newValue.email || newValue.external_id || '';
      },
    },
  },
  methods: {
    clearSearch() {
      this.searchQuery = '';
      this.results = [];
      this.showDropdown = false;
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },
    onInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const query = (this.searchQuery || '').trim();
      if (!query) {
        this.results = [];
        this.showDropdown = false;
        this.$emit('clear');
        return;
      }

      this.showDropdown = true;
      this.searchDebounceTimer = setTimeout(() => {
        this.searchEntities(query);
      }, this.debounceMs);
    },
    onFocus() {
      if ((this.searchQuery || '').trim()) {
        this.showDropdown = true;
      }
    },
    onBlur() {
      setTimeout(() => {
        this.showDropdown = false;
        if (this.current) {
          this.searchQuery = this.current.name || this.current.email || this.current.external_id || '';
        } else {
          this.searchQuery = '';
        }
      }, 120);
    },
    async searchEntities(query) {
      this.isSearching = true;
      try {
        const params = new URLSearchParams({
          search: query,
          page_size: String(this.pageSize),
        });
        const response = await fetch(`/api/v1/entities/get_all/?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to search entities');
        }
        const data = await response.json();
        this.results = data.items || [];
      } catch (error) {
        console.error(error);
        this.results = [];
      } finally {
        this.isSearching = false;
      }
    },
    selectResult(option) {
      this.searchQuery = option?.name || option?.email || '';
      this.showDropdown = false;
      this.results = [];
      this.$emit('select', option);
    },
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
};
</script>

<style scoped>
.entity-search-wrap {
  position: relative;
  z-index: 1;
  isolation: isolate;
}

.entity-dropdown {
  position: absolute;
  z-index: 9999;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: var(--stk-scheme-main, #ffffff);
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  max-height: 280px;
  overflow-y: auto;
  opacity: 1;
  font: inherit;
}

.entity-dropdown-item {
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--stk-size-3);
  background-color: var(--stk-scheme-main, #ffffff);
  border: 0;
  color: var(--stk-color-text, inherit);
  font: inherit;
  line-height: 1.35;
  cursor: pointer;
  transition: background-color 120ms ease;
}

.entity-dropdown-item:not(.muted):hover {
  background-color: var(--stk-color-background-soft, #f3f4f6);
}

.entity-dropdown-item.muted {
  color: var(--stk-color-text-muted);
  cursor: default;
}

.entity-name {
  font-weight: 600;
}
</style>
